import React, { useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell } from 'recharts';
import { Wallet, Receipt } from 'phosphor-react';
import { useFetch } from '_api';
import { formatVND, formatDate, toYmd } from '_helpers';
import { PageHeader, Bezel, StatCard, StatusPill, Table, PeriodPicker, LoadingScreen, MotionReveal } from '_ui';

const now = new Date();
const BRASS = '#B08D57';

// Commission per calendar day, from the month's paid invoices (grouped by paidAt).
function dailySeries(invoices = []) {
    const byDay = new Map();
    invoices.forEach(inv => {
        const d = new Date(inv.paidAt || inv.createdAt);
        const day = d.getDate();
        byDay.set(day, (byDay.get(day) || 0) + inv.totalCommission);
    });
    return [...byDay.entries()].sort((a, b) => a[0] - b[0]).map(([day, commission]) => ({ label: `${day}`, commission }));
}

function ChartTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-xl bg-espresso px-3 py-2 text-cream shadow-ambient">
            <p className="text-[11px] uppercase tracking-wide text-cream/60">Ngày {label}</p>
            <p className="tnum text-sm font-semibold">{formatVND(payload[0].value)}</p>
        </div>
    );
}

export function LuongCuaToi() {
    const [view, setView] = useState({ mode: 'month', date: toYmd(now), period: { month: now.getMonth() + 1, year: now.getFullYear() } });
    const query = view.mode === 'day' ? `date=${view.date}` : `month=${view.period.month}&year=${view.period.year}`;
    const { data, loading } = useFetch(`/api/payouts/me?${query}`);

    if (loading) return <LoadingScreen />;
    const p = data || { totalRevenue: 0, totalCommission: 0, count: 0, payoutStatus: 'pending', paidAt: null, invoices: [] };
    const series = dailySeries(p.invoices);

    const columns = [
        { key: 'paidAt', header: 'Ngày', render: r => <span className="tnum text-espresso-soft">{formatDate(r.paidAt || r.createdAt)}</span> },
        { key: 'customer', header: 'Khách', render: r => r.customerName || <span className="text-espresso-soft/50">—</span> },
        { key: 'total', header: 'Doanh thu', align: 'right', render: r => <span className="tnum">{formatVND(r.totalPrice)}</span> },
        { key: 'commission', header: 'Hoa hồng', align: 'right', render: r => <span className="tnum font-medium text-sage-deep">{formatVND(r.totalCommission)}</span> },
    ];

    return (
        <div className="space-y-10">
            <PageHeader
                eyebrow="Nhân viên"
                title="Lương của tôi"
                description={view.mode === 'day' ? 'Hoa hồng được tính trên các hoá đơn đã thanh toán trong ngày.' : 'Hoa hồng được tính trên các hoá đơn đã thanh toán trong tháng.'}
                actions={<PeriodPicker value={view} onChange={setView} />}
            />

            {/* Hero commission + status */}
            <MotionReveal>
                <Bezel className="flex flex-col gap-6 p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10">
                    <div>
                        <span className="text-[11px] font-semibold uppercase tracking-eyebrow text-espresso-soft/70">Hoa hồng dự kiến</span>
                        <p className="mt-3 font-serif text-6xl leading-none text-sage-deep tnum">{formatVND(p.totalCommission)}</p>
                        <p className="mt-3 text-sm text-espresso-soft/80">Trên doanh thu {formatVND(p.totalRevenue)} · {p.count} hoá đơn đã thu</p>
                    </div>
                    <div className="flex flex-col items-start gap-2 sm:items-end">
                        <span className="text-[11px] font-semibold uppercase tracking-eyebrow text-espresso-soft/70">Trạng thái lương</span>
                        <StatusPill status={p.payoutStatus} paidLabel="Đã trả lương" pendingLabel="Chưa trả" />
                        {p.paidAt && <span className="text-xs text-espresso-soft/70">Trả ngày {formatDate(p.paidAt)}</span>}
                    </div>
                </Bezel>
            </MotionReveal>

            <div className="grid gap-5 sm:grid-cols-2">
                <MotionReveal><StatCard label="Doanh thu đã thu" value={formatVND(p.totalRevenue)} icon={Wallet} /></MotionReveal>
                <MotionReveal delay={0.05}><StatCard label="Số hoá đơn" value={p.count} icon={Receipt} /></MotionReveal>
            </div>

            {/* Chart — only meaningful across a month; a single day has one point. */}
            {view.mode === 'month' && (
                <MotionReveal>
                    <Bezel className="p-6 sm:p-8">
                        <h3 className="font-serif text-2xl text-espresso">Hoa hồng theo ngày</h3>
                        {series.length === 0 ? (
                            <p className="py-12 text-center text-sm text-espresso-soft/60">Chưa có hoá đơn đã thanh toán trong tháng.</p>
                        ) : (
                            <div className="mt-6 h-64 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={series} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                                        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: '#5A4F44', fontSize: 12 }} />
                                        <Tooltip cursor={{ fill: 'rgba(43,33,24,0.04)' }} content={<ChartTooltip />} />
                                        <Bar dataKey="commission" radius={[8, 8, 8, 8]} maxBarSize={36}>
                                            {series.map((_, i) => <Cell key={i} fill={BRASS} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </Bezel>
                </MotionReveal>
            )}

            <Bezel className="overflow-hidden">
                <Table columns={columns} rows={p.invoices} empty={view.mode === 'day' ? 'Chưa có hoá đơn đã thanh toán trong ngày.' : 'Chưa có hoá đơn đã thanh toán.'} />
            </Bezel>
        </div>
    );
}
